import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import sanitizeHtml from 'sanitize-html';
import { isBoolean, isEmpty, isNumber } from 'lodash';

import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { closeNotification } from 'redux-store/reducers/common-reducer';

import './notification.scss';

import type {
  Notification as NotificationAlias,
  NotificationCloseReason
} from 'types';

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['strong', 'a', 'div'],
  allowedAttributes: {
    a: ['href', 'onclick', 'target', 'rel'],
    div: ['class']
  }
};

const ICONS = {
  info: 'info-inside-circle-custom-bg',
  success: 'checkmark-inside-circle-custom-bg',
  danger: 'exclamation-inside-circle-custom-bg',
  warning: 'exclamation-inside-circle-custom-bg'
};

const NOTIFICATION_SUCCESS_HIDE_DURATION = 5000;

type OwnProps = {};

type StateProps = {
  notification: NotificationAlias
};

type DispatchProps = {
  dispatchCloseNotification: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  open: boolean
};

export class Notification extends React.PureComponent<Props, State> {
  state = {
    open: !isEmpty(this.props.notification) && Boolean(this.props.notification.message) || false
  };

  handleCloseButtonRefCallback = (ref: HTMLDivElement): void => {
    const { notification } = this.props;
    const closeButtonRefCallback = notification.closeButtonRefCallback;

    if (closeButtonRefCallback) {
      closeButtonRefCallback(ref);
    }
  };

  handleClickAway = () => {
    const { notification } = this.props;

    if (notification.keepBetweenPages) {
      this.handleOpen();
    }
  };

  handleOpen = (): void => {
    this.setState({
      open: true
    });
  };

  handleGenericClose = () => {
    const { notification, dispatchCloseNotification } = this.props;
    const { onClose } = notification;

    this.setState({
      open: false
    });

    if (onClose) {
      onClose();
    }

    dispatchCloseNotification();
  };

  handleClose = (event: Event | React.SyntheticEvent<any, Event>, reason: NotificationCloseReason): void => {
    if (reason === 'clickaway') {
      return;
    }

    this.handleGenericClose();
  };

  renderClose(): React.ReactNode | undefined | null {
    const { notification } = this.props;
    const showClose = isBoolean(notification.showClose) ? notification.showClose : true;

    if (!showClose) {
      return null;
    }

    return (
      <div
        ref={this.handleCloseButtonRefCallback}
        className="notification__close"
        onClick={this.handleGenericClose}
      >
        <SVGDynamicIcon
          name="cross-custom-fg"
          width={12}
          height={12}
          customColor="white"
        />
      </div>
    );
  }

  renderMessage(): React.ReactNode {
    const { notification } = this.props;
    const showIcon = isBoolean(notification.showIcon) ? notification.showIcon : true;

    return (
      <div className="notification__message">
        {
          showIcon && (
            <div className="notification__icon">
              <SVGDynamicIcon
                name={ICONS[notification.type]}
                width={12}
                height={12}
              />
            </div>
          )
        }
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(notification.message, SANITIZE_HTML_CONFIG) }} />
      </div>
    );
  }

  renderNotification(): JSX.Element {
    const { notification } = this.props;
    const { open } = this.state;
    const type = notification.type || 'info';
    const position = notification.position || { vertical: 'bottom', horizontal: 'left' };
    let hideDuration = isNumber(notification.hideDuration) ? notification.hideDuration : null;

    if (notification.type === 'success') {
      hideDuration = isNumber(notification.hideDuration) ? notification.hideDuration : NOTIFICATION_SUCCESS_HIDE_DURATION;
    }

    const classes = classnames('notification__container', {
      [`notification__container--type-${type}`]: Boolean(type)
    });

    return (
      <Snackbar
        anchorOrigin={position}
        open={open}
        autoHideDuration={hideDuration}
        onClose={this.handleClose}
      >
        <SnackbarContent
          className={classes}
          action={this.renderClose()}
          message={this.renderMessage()}
        />
      </Snackbar>
    );
  }

  render(): React.ReactNode {
    return (
      <div
        className="notification"
        data-qa="notification"
      >
        <ClickAwayListener onClickAway={this.handleClickAway}>
          {this.renderNotification()}
        </ClickAwayListener>
      </div>
    );
  }
}

const mapStateToProps = ({ common }): StateProps => ({
  notification: common.notification
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCloseNotification: () => (
    dispatch(closeNotification())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(Notification);
